/**
 * NotificationPermissionBanner - Shows when notifications need permission
 *
 * Appears when:
 * - A notification would be shown but permission is 'default'
 * - User hasn't dismissed it this session
 *
 * Features:
 * - "Enable" button triggers browser permission prompt
 * - "Not Now" button hides for current session
 * - Auto-hides when permission is granted
 * - Matches app theme with gradient background and red button
 */

import { useCallback } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { useI18n } from '../context/I18nContext';
import './NotificationPermissionBanner.css';

const NotificationPermissionBanner = () => {
  const { t } = useI18n();
  const {
    permissionStatus,
    showPermissionBanner,
    requestPermission,
    dismissPermissionBanner,
  } = useNotifications();

  const handleEnable = useCallback(async () => {
    await requestPermission();
    // Always dismiss after user clicks Enable:
    // - granted: notifications work, banner not needed
    // - denied: permissionStatus updates, Settings shows "Blocked" instructions
    // - default (prompt dismissed): bannerDismissedThisSession prevents re-show
    dismissPermissionBanner();
  }, [requestPermission, dismissPermissionBanner]);

  const handleDismiss = useCallback(() => {
    dismissPermissionBanner();
  }, [dismissPermissionBanner]);

  // Don't show if permission is not 'default' or banner is dismissed
  if (permissionStatus !== 'default' || !showPermissionBanner) {
    return null;
  }

  return (
    <div className="notification-permission-banner">
      <div className="notification-permission-banner-content">
        <ion-icon name="notifications-outline" className="banner-icon" />
        <div className="banner-text">
          <span className="banner-title">
            {t('Notifications.permissionBannerTitle') || 'Enable Notifications'}
          </span>
          <span className="banner-description">
            {t('Notifications.permissionBannerDesc') || 'Click Enable and allow in your browser to get notified of new messages'}
          </span>
        </div>
        <div className="notification-permission-banner-actions">
          <button className="banner-dismiss-btn" onClick={handleDismiss}>
            {t('Notifications.notNow') || 'Not Now'}
          </button>
          <button className="banner-enable-btn" onClick={handleEnable}>
            {t('Notifications.enable') || 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionBanner;
